import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { apiClient } from '../api/client';
import { Button, QuestionCard, Card, CardContent, LoadingSpinner } from '../components/ui';
import { ArrowLeft, ArrowRight, CheckCircle, Zap } from 'lucide-react';
import ProctorCamera from '../components/ProctorCamera';
import ProctorWarnings from '../components/ProctorWarnings';
import ProctorDashboard from '../components/ProctorDashboard';
import { useProctoring } from '../hooks/useProctoring';
import { stopAllCameraStreams } from '../utils/cameraUtils';
import toast from 'react-hot-toast';

const ProductivityTest = () => {
  const [testData, setTestData] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [cameraActive, setCameraActive] = useState(false);
  const [questionStartTime, setQuestionStartTime] = useState(Date.now());
  const [timeTaken, setTimeTaken] = useState({});
  const { user, token } = useAuth();
  const navigate = useNavigate();

  // Auto-save key
  const STORAGE_KEY = `productivity_progress_${user?.uid}`;

  // Proctoring system
  const {
    metrics,
    warnings,
    trackQuestionResponse,
    getIntegrityScore,
    getViolationSummary,
    addWarning
  } = useProctoring();

  // Load saved progress on mount
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const { answers: savedAnswers, currentQuestion: savedQuestion } = JSON.parse(saved);
        setAnswers(savedAnswers || {});
        setCurrentQuestion(savedQuestion || 0);
        toast.success('Progress restored');
      } catch (e) {
        console.error('Failed to restore progress:', e);
      }
    }
  }, [user]);

  // Auto-save progress whenever answers change
  useEffect(() => {
    if (Object.keys(answers).length > 0) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ answers, currentQuestion }));
    }
  }, [answers, currentQuestion]);

  const startTest = useCallback(async () => {
    if (!cameraActive) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await apiClient.post('/test/productivity/start', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTestData(response.data);
    } catch (error) {
      console.error('Failed to start test:', error);
      toast.error('Failed to start test');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  }, [token, navigate, cameraActive]);

  useEffect(() => {
    if (user && token && cameraActive) {
      startTest();
    }
  }, [user, token, cameraActive, startTest]);

  // Handle camera loss - only redirect if test has actually started
  const handleCameraLost = (reason) => {
    // Don't redirect if test hasn't loaded yet or is still loading
    if (testData && !loading) {
      setTimeout(() => {
        toast.error('Assessment terminated: Camera access required');
        addWarning('Assessment terminated: ' + reason);
        navigate('/assessment');
      }, 2000);
    }
  };

  // Prevent test if camera lost during test
  useEffect(() => {
    if (testData && !loading && !cameraActive) {
      const timer = setTimeout(() => {
        toast.error('Camera access is required for proctored assessment');
        navigate('/assessment');
      }, 5000); // Give 5 seconds grace period
      return () => clearTimeout(timer);
    }
  }, [cameraActive, testData, loading, navigate]);

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'Enter' && answers.hasOwnProperty(currentQuestion)) {
        nextQuestion();
      }
    };
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [currentQuestion, answers]);

  const handleAnswer = (value) => {
    const timeSpent = Math.floor((Date.now() - questionStartTime) / 1000);
    setAnswers(prev => ({
      ...prev,
      [currentQuestion]: value
    }));
    setTimeTaken(prev => ({
      ...prev,
      [currentQuestion]: timeSpent
    }));
    
    // Track response for proctoring
    if (testData?.questions?.[currentQuestion]) {
      trackQuestionResponse(testData.questions[currentQuestion].id, value);
    }
  };

  const nextQuestion = () => {
    if (currentQuestion < testData.questions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
      setQuestionStartTime(Date.now());
    } else {
      submitTest();
    }
  };

  const previousQuestion = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(prev => prev - 1);
      setQuestionStartTime(Date.now());
    }
  };

  const submitTest = async () => {
    setSubmitting(true);
    try {
      // Convert answers object to array format for backend
      const answersArray = [];
      for (let i = 0; i < testData.questions.length; i++) {
        answersArray[i] = answers[i];
      }
      
      const response = await apiClient.post('/tests/productivity/submit', {
        answers: answersArray,
        deviceInfo: {
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString()
        },
        sessionData: {
          startTime: testData.startTime || new Date().toISOString(),
          completedAt: new Date().toISOString()
        }
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Clear saved progress after successful submission
      localStorage.removeItem(STORAGE_KEY);
      // Clear assignment invite ref after successful final submit.
      sessionStorage.removeItem('assessment_ref');
      
      // Stop camera before navigation
      stopAllCameraStreams();
      
      toast.success('Assessment completed! Thank you for your participation.');
      setTimeout(() => {
        navigate('/thank-you');
      }, 1500);
    } catch (error) {
      console.error('Failed to submit test:', error);
      toast.error('Failed to submit test');
    } finally {
      setSubmitting(false);
    }
  };

  const progress = testData ? Math.round(((currentQuestion + 1) / testData.questions.length) * 100) : 0;

  if (loading || !cameraActive) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-teal-100 flex items-center justify-center">
        <ProctorCamera 
          onCameraStatus={setCameraActive} 
          onCameraLost={handleCameraLost}
        />
        <motion.div 
          className="text-center"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6 }}
        >
          <LoadingSpinner size="xl" className="mb-6" />
          <motion.h2 
            className="text-2xl font-bold text-gray-800 mb-4"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            Loading Productivity Assessment
          </motion.h2>
          <motion.p 
            className="text-gray-600"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            {!cameraActive ? 'Waiting for camera access...' : 'Preparing your productivity analysis...'}
          </motion.p>
          {!cameraActive && (
            <motion.div
              className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4 max-w-md"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7 }}
            >
              <p className="text-sm text-yellow-800">
                📹 Camera access is required for proctored assessment. Please enable camera permissions.
              </p>
            </motion.div>
          )}
        </motion.div>
      </div>
    );
  }

  if (!testData) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Failed to load test. Please try again.</p>
        <button
          onClick={() => navigate('/dashboard')}
          className="mt-4 text-emerald-600 hover:text-emerald-700 font-semibold"
        >
          Return to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gradient-to-br from-green-50 via-teal-50 to-blue-50 flex flex-col">
      {/* Proctoring Components */}
      <ProctorCamera 
        onCameraStatus={setCameraActive} 
        onCameraLost={handleCameraLost}
      />
      <ProctorWarnings warnings={warnings} onDismiss={() => {}} />
      <ProctorDashboard 
        metrics={metrics}
        integrityScore={getIntegrityScore()}
        violations={getViolationSummary()}
      />
      
      <div className="max-w-4xl mx-auto px-4 flex-1 flex flex-col justify-center">
        {/* Header */}
        <motion.div 
          className="text-center mb-6"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex items-center justify-center mb-2">
            <Zap className="w-6 h-6 text-green-600 mr-2" />
            <h1 className="text-2xl font-bold text-gray-900">
              Productivity Assessment
            </h1>
          </div>
          <div className="flex items-center justify-center space-x-4 text-sm text-gray-600">
            <span>Question {currentQuestion + 1} of {testData.questions.length}</span>
            {testData?.questions?.[currentQuestion]?.category && (
              <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                {testData.questions[currentQuestion].category}
              </span>
            )}
          </div>
        </motion.div>

        {/* Question Card */}
        <AnimatePresence mode="wait">
          <QuestionCard
            key={currentQuestion}
            question={testData.questions[currentQuestion]}
            currentQuestion={currentQuestion + 1}
            totalQuestions={testData.questions.length}
            selectedAnswer={answers[currentQuestion]}
            onAnswerSelect={handleAnswer}
          />
        </AnimatePresence>

        {/* Navigation */}
        <motion.div 
          className="flex justify-between items-center mt-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Button
            onClick={previousQuestion}
            disabled={currentQuestion === 0}
            variant="outline"
            size="lg"
            className="flex items-center space-x-2"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Previous</span>
          </Button>
          
          {currentQuestion === testData.questions.length - 1 ? (
            <Button
              onClick={nextQuestion}
              disabled={!answers.hasOwnProperty(currentQuestion) || submitting}
              loading={submitting}
              variant="success"
              size="lg"
              className="flex items-center space-x-2"
            >
              <CheckCircle className="w-4 h-4" />
              <span>{submitting ? 'Analyzing...' : 'Complete Assessment'}</span>
            </Button>
          ) : (
            <Button
              onClick={nextQuestion}
              disabled={!answers.hasOwnProperty(currentQuestion)}
              variant="default"
              size="lg"
              className="flex items-center space-x-2"
            >
              <span>Next</span>
              <ArrowRight className="w-4 h-4" />
            </Button>
          )}
        </motion.div>


      </div>
    </div>
  );
};

export default ProductivityTest;
